import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentItemsAPI, contentProjectsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Layers, FileEdit, Hammer, Rocket, Archive, DollarSign, AlertTriangle, Receipt,
  Clock, ShieldAlert, TrendingUp, HelpCircle, Flame, ChevronDown, ChevronUp, TimerReset,
  ListChecks, CalendarClock, Copy, FolderKanban,
} from 'lucide-react';
import { toast } from 'sonner';

const TRACK_COLORS = ['#84cc16', '#3b82f6', '#f59e0b'];
const TECH_COLORS = ['#84cc16', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#64748b'];
const PRIORITY_COLORS = { P1: '#ef4444', P2: '#f59e0b', P3: '#64748b', Unset: '#cbd5e1' };

const TRIAGE_PRIORITY_STYLES = {
  high: { label: 'High', className: 'bg-red-100 text-red-700 border-red-300' },
  medium: { label: 'Medium', className: 'bg-amber-100 text-amber-700 border-amber-300' },
  low: { label: 'Low', className: 'bg-slate-100 text-slate-600 border-slate-300' },
};

export const ContentDevelopmentDashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [overdueProjects, setOverdueProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slippageExpanded, setSlippageExpanded] = useState(false);
  const [selectedTriage, setSelectedTriage] = useState([]);
  const [bulkResolveOpen, setBulkResolveOpen] = useState(false);
  const [bulkResolutionNote, setBulkResolutionNote] = useState('');
  const [bulkResolving, setBulkResolving] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await contentItemsAPI.getDashboardStats();
      setStats(data);
    } catch { toast.error('Failed to load content dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    contentProjectsAPI.getAll().then(ps => {
      setOverdueProjects(ps.filter(p => p.target_delivery_date && p.target_delivery_date < today && (p.status_rollup?.in_build || 0) > 0));
    }).catch(() => {});
  }, []);

  const toggleTriageSelection = (t) => {
    setSelectedTriage(prev => prev.some(s => s.id === t.id)
      ? prev.filter(s => s.id !== t.id)
      : [...prev, { id: t.id, entry_id: t.entry_id }]);
  };

  const handleBulkResolve = async () => {
    setBulkResolving(true);
    try {
      const items = selectedTriage.map(s => ({ content_item_id: s.id, entry_id: s.entry_id }));
      const result = await contentItemsAPI.bulkResolveTriage(items, bulkResolutionNote.trim() || null);
      toast.success(`Resolved ${result.resolved_count} triage item(s)`);
      setSelectedTriage([]);
      setBulkResolveOpen(false);
      setBulkResolutionNote('');
      load();
    } catch { toast.error('Failed to bulk-resolve triage items'); }
    finally { setBulkResolving(false); }
  };

  const handleCopyScrumSummary = async () => {
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const triageLines = (stats.triage_queue || []).length
      ? stats.triage_queue.map(t => `- [${(t.priority || 'medium').toUpperCase()}] ${t.title} — ${t.reason} (Open ${t.days_open === 0 ? 'today' : t.days_open + 'd'})`).join('\n')
      : '- Nothing flagged.';
    const deliveryLines = (stats.upcoming_deliveries_risk || []).length
      ? stats.upcoming_deliveries_risk.map(u => `- ${u.title} — ${u.current_phase_label} · Delivery ${u.delivery_date} (${u.days_to_delivery === 0 ? 'today' : u.days_to_delivery + 'd away'})`).join('\n')
      : '- None at risk.';
    const summary = `SCRUM Triage Summary — ${today}\n\nNEEDS TRIAGE (${stats.triage_queue?.length || 0})\n${triageLines}\n\nDELIVERY COMING UP (${stats.upcoming_deliveries_risk?.length || 0})\n${deliveryLines}`;
    try {
      await navigator.clipboard.writeText(summary);
      toast.success('SCRUM summary copied to clipboard');
    } catch { toast.error('Failed to copy — clipboard access blocked'); }
  };

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-muted animate-pulse rounded-lg" />)}
      </div>
    );
  }

  const byStatus = stats.by_catalog_status || {};
  const filteredTriageQueue = priorityFilter === 'all'
    ? (stats.triage_queue || [])
    : (stats.triage_queue || []).filter(t => (t.priority || 'medium') === priorityFilter);
  const po = stats.po_summary || {};
  const byProjection = stats.by_availability_projection || {};

  const kpis = [
    { label: 'Total Content Items', value: stats.total_items, icon: Layers, color: 'text-lime' },
    { label: 'Proposed', value: byStatus.proposed || 0, icon: FileEdit, color: 'text-blue-500' },
    { label: 'In Build', value: byStatus.in_build || 0, icon: Hammer, color: 'text-amber-500' },
    { label: 'Available (Live)', value: byStatus.available || 0, icon: Rocket, color: 'text-green-500' },
    { label: 'Retired', value: byStatus.retired || 0, icon: Archive, color: 'text-gray-500' },
  ];

  const projectionCards = [
    { key: 'on_track', label: 'On Track', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
    { key: 'at_risk', label: 'At Risk (\u226414 days)', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { key: 'overdue', label: 'Overdue', icon: ShieldAlert, color: 'text-red-600', bg: 'bg-red-50' },
    { key: 'no_target_set', label: 'No Target Set', icon: HelpCircle, color: 'text-gray-500', bg: 'bg-gray-50' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-dashboard-page">
      <div>
        <h1 className="text-3xl font-heading font-bold">Content Development Dashboard</h1>
        <p className="text-muted-foreground">Microsoft partner content lifecycle — build, review, publish &amp; syndicate</p>
      </div>

      {overdueProjects.length > 0 && (
        <Card className="border-red-400 bg-red-50" data-testid="overdue-projects-widget">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-900">
              <FolderKanban className="w-4 h-4" />Overdue Projects ({overdueProjects.length})
            </CardTitle>
            <p className="text-xs text-red-700">Projects past their target delivery date with items still in build</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {overdueProjects.map(p => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/content-projects/${p.id}`)}
                  className="w-full flex items-center justify-between gap-3 text-left bg-white border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50/50 transition-colors"
                  data-testid={`overdue-project-item-${p.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-red-900 truncate">{p.name}</p>
                    <p className="text-xs text-red-700">
                      Target {p.target_delivery_date} · {p.status_rollup?.in_build || 0} of {p.status_rollup?.total || 0} still in build
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs flex-shrink-0 border-red-300 text-red-700">Overdue</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(stats.triage_queue?.length > 0 || stats.upcoming_deliveries_risk?.length > 0) && (
        <Card className="border-2 border-lime bg-lime/5" data-testid="triage-queue-scrum-card">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-lime-700" />Triage Queue for Next SCRUM
                </CardTitle>
                <p className="text-xs text-muted-foreground">Items needing your decision, plus courses with a delivery coming up that aren't build-complete yet</p>
              </div>
              <Button size="sm" variant="outline" onClick={handleCopyScrumSummary} className="flex-shrink-0" data-testid="copy-scrum-summary-btn">
                <Copy className="w-3.5 h-3.5 mr-1.5" />Copy SCRUM Summary
              </Button>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />Needs Triage ({filteredTriageQueue.length})
                </h3>
                <div className="flex items-center gap-2">
                  {selectedTriage.length > 0 && (
                    <Button size="sm" onClick={() => setBulkResolveOpen(true)} className="h-6 text-xs bg-lime text-lime-foreground hover:bg-lime/90" data-testid="resolve-selected-triage-btn">
                      Resolve Selected ({selectedTriage.length})
                    </Button>
                  )}
                  <button onClick={() => navigate('/content-items?flag=triage')} className="text-xs text-amber-700 hover:underline font-medium" data-testid="triage-view-in-tracker-btn">
                    View in tracker →
                  </button>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="h-6 text-xs w-[110px]" data-testid="triage-priority-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="high">High Only</SelectItem>
                      <SelectItem value="medium">Medium Only</SelectItem>
                      <SelectItem value="low">Low Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {filteredTriageQueue.length > 0 ? (
                <div className="space-y-2">
                  {filteredTriageQueue.map(t => (
                    <div
                      key={t.id}
                      className="w-full flex items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-2 hover:bg-amber-50/50 transition-colors"
                      data-testid={`triage-queue-item-${t.id}`}
                    >
                      <Checkbox
                        checked={selectedTriage.some(s => s.id === t.id)}
                        onCheckedChange={() => toggleTriageSelection(t)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`triage-queue-select-${t.id}`}
                      />
                      <button onClick={() => navigate(`/content-items/${t.id}`)} className="flex-1 flex items-center justify-between gap-3 text-left min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${TRIAGE_PRIORITY_STYLES[t.priority || 'medium'].className}`}>
                              {TRIAGE_PRIORITY_STYLES[t.priority || 'medium'].label}
                            </Badge>
                            <p className="text-sm font-medium truncate">{t.title}</p>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{t.reason}</p>
                        </div>
                        <Badge variant="outline" className="text-xs flex-shrink-0 border-amber-300 text-amber-700">
                          {t.days_open === 0 ? 'Today' : `Open ${t.days_open}d`}
                        </Badge>
                      </button>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground py-4">{priorityFilter === 'all' ? 'Nothing flagged right now.' : `No ${priorityFilter} priority items right now.`}</p>}
            </div>
            <div>
              <h3 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5" />Delivery Coming Up ({stats.upcoming_deliveries_risk?.length || 0})
              </h3>
              {stats.upcoming_deliveries_risk?.length > 0 ? (
                <div className="space-y-2">
                  {stats.upcoming_deliveries_risk.map(u => (
                    <button
                      key={u.id}
                      onClick={() => navigate(`/content-items/${u.id}`)}
                      className="w-full flex items-center justify-between gap-3 text-left bg-white border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-50/50 transition-colors"
                      data-testid={`upcoming-delivery-risk-item-${u.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{u.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.current_phase_label} · Delivery {u.delivery_date}</p>
                      </div>
                      <Badge variant="outline" className="text-xs flex-shrink-0 border-blue-300 text-blue-700">
                        {u.days_to_delivery === 0 ? 'Today' : `${u.days_to_delivery}d away`}
                      </Badge>
                    </button>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground py-4">No at-risk upcoming deliveries.</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {stats.slippage_risk?.length > 0 && (
        <Card className="border-orange-400 bg-orange-50" data-testid="content-dashboard-slippage-risk-card">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => setSlippageExpanded(v => !v)}
                className="flex-1 flex items-center justify-between text-left"
                data-testid="slippage-risk-toggle-button"
              >
                <p className="flex items-center gap-2 text-orange-900 font-semibold">
                  <TimerReset className="w-4 h-4" />
                  <span data-testid="slippage-risk-count">{stats.slippage_risk.length}</span>
                  {' '}P1 content item{stats.slippage_risk.length > 1 ? 's' : ''} at risk of slippage
                </p>
                {slippageExpanded ? <ChevronUp className="w-4 h-4 text-orange-700" /> : <ChevronDown className="w-4 h-4 text-orange-700" />}
              </button>
              <button onClick={() => navigate('/content-items?flag=slippage')} className="text-xs text-orange-700 hover:underline font-medium whitespace-nowrap ml-3" data-testid="slippage-view-in-tracker-btn">
                View in tracker →
              </button>
            </div>
            {slippageExpanded && (
              <div className="space-y-2 mt-3" data-testid="slippage-risk-list">
                {stats.slippage_risk.map(p => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/content-items/${p.id}`)}
                    className="w-full flex items-center justify-between gap-3 text-left bg-white border border-orange-200 rounded-lg px-3 py-2 hover:bg-orange-50/50 transition-colors"
                    data-testid={`slippage-risk-item-${p.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-orange-900 truncate">{p.title}</p>
                      <p className="text-xs text-orange-700">{p.reason}</p>
                    </div>
                    {p.go_live_slipped_days > 0 && (
                      <Badge variant="outline" className="text-xs flex-shrink-0 border-orange-300 text-orange-700">
                        Slipped {p.go_live_slipped_days}d
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {stats.p1_at_risk?.length > 0 && (
        <Card className="border-red-500 bg-red-50" data-testid="content-dashboard-p1-at-risk-alert">
          <CardContent className="pt-5">
            <p className="flex items-center gap-2 text-red-900 font-semibold mb-3">
              <Flame className="w-4 h-4" />{stats.p1_at_risk.length} P1 (Highest Priority) build{stats.p1_at_risk.length > 1 ? 's' : ''} falling behind schedule
            </p>
            <div className="space-y-2">
              {stats.p1_at_risk.map(p => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/content-items/${p.id}`)}
                  className="w-full flex items-center justify-between gap-3 text-left bg-white border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50/50 transition-colors"
                  data-testid={`p1-at-risk-item-${p.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-red-900 truncate">{p.title}</p>
                    <p className="text-xs text-red-700">{p.reason}</p>
                  </div>
                  <Badge variant="outline" className="text-xs flex-shrink-0 border-red-300 text-red-700">{p.current_phase_label}</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {po.exhausted_count > 0 && (
        <Card className="border-red-400 bg-red-50 cursor-pointer" onClick={() => navigate('/content-pos')} data-testid="content-dashboard-exhausted-po-alert">
          <CardContent className="pt-5">
            <p className="flex items-center gap-2 text-red-900 font-medium mb-2">
              <AlertTriangle className="w-4 h-4" />{po.exhausted_count} Content PO{po.exhausted_count > 1 ? 's' : ''} fully utilized
            </p>
            <div className="flex flex-wrap gap-2">
              {po.exhausted_pos?.map(p => (
                <span key={p.id} className="flex items-center gap-2 text-sm bg-white border border-red-200 rounded-full px-3 py-1.5 text-red-800 font-medium">
                  {p.po_number}<Badge variant="destructive" className="text-xs">${p.used_amount.toLocaleString()} used</Badge>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="border border-border/50" data-testid={`content-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="py-4 px-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-muted-foreground">{label}</p>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <p className="text-2xl font-bold">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="text-sm font-heading font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Availability Projections</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {projectionCards.map(({ key, label, icon: Icon, color, bg }) => (
            <Card
              key={key}
              className={`border-0 ${bg} cursor-pointer hover:ring-2 hover:ring-lime/40 transition-shadow`}
              data-testid={`content-projection-${key}`}
              onClick={() => navigate(`/content-items?availability=${key}`)}
              role="button"
            >
              <CardContent className="py-4 px-4">
                <div className="flex items-center justify-between mb-1">
                  <p className={`text-xs font-medium ${color}`}>{label}</p>
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                <p className={`text-2xl font-bold ${color}`}>{byProjection[key] || 0}</p>
                <p className="text-[11px] text-muted-foreground mt-1">View items →</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-lime" data-testid="content-po-total-contracted">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />Total PO Contracted</p>
            <p className="text-2xl font-bold mt-1">${po.total_contracted?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-400" data-testid="content-po-total-used">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />Total PO Used</p>
            <p className="text-2xl font-bold mt-1">${po.total_used?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-400" data-testid="content-po-total-remaining">
          <CardContent className="pt-5">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Receipt className="w-3.5 h-3.5" />Total PO Remaining</p>
            <p className="text-2xl font-bold mt-1">${po.total_remaining?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Content Items by Technology</CardTitle></CardHeader>
          <CardContent>
            {stats.by_technology?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.by_technology} margin={{ top: 4, right: 4, bottom: 40, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {stats.by_technology.map((entry, idx) => <Cell key={entry.name} fill={TECH_COLORS[idx % TECH_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground py-10 text-center">No data yet</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Content Items by Current Phase</CardTitle></CardHeader>
          <CardContent>
            {stats.by_phase?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.by_phase} margin={{ top: 4, right: 4, bottom: 40, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#84cc16" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground py-10 text-center">No data yet</p>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Content Items by Track</CardTitle></CardHeader>
        <CardContent>
          {stats.by_track?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.by_track} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stats.by_track.map((entry, idx) => <Cell key={entry.name} fill={TRACK_COLORS[idx % TRACK_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">No data yet</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Content Items by Microsoft Priority (P1/P2/P3)</CardTitle></CardHeader>
        <CardContent>
          {stats.by_priority?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.by_priority} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={60} />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stats.by_priority.map((entry) => <Cell key={entry.name} fill={PRIORITY_COLORS[entry.name] || '#94a3b8'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">No data yet</p>}
        </CardContent>
      </Card>

      <Dialog open={bulkResolveOpen} onOpenChange={setBulkResolveOpen}>
        <DialogContent data-testid="bulk-resolve-triage-dialog">
          <DialogHeader>
            <DialogTitle>Resolve {selectedTriage.length} Triage Item{selectedTriage.length !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>Optionally add one shared decision note applied to all selected items.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Resolution Note (optional)</Label>
            <Textarea
              value={bulkResolutionNote}
              onChange={e => setBulkResolutionNote(e.target.value)}
              placeholder="e.g. Reviewed in SCRUM — proceeding as planned"
              rows={3}
              data-testid="bulk-resolution-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkResolveOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkResolve} disabled={bulkResolving} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="bulk-resolve-confirm-btn">
              {bulkResolving ? 'Resolving...' : 'Mark Resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentDevelopmentDashboardPage;
